// NOTE: The backend API paths below intentionally keep the legacy `influencer`
// segment (e.g. /api/influencer/...) so the frontend and backend can deploy
// independently during the affiliate rebrand. Do not rename them.
import { apiFetch } from "../../core/utils/api";
import { isAffiliatePortalBlockedError } from "./affiliatePortalMessages";

const readResponseJsonSafe = async (response) => {
  try {
    return await response.json();
  } catch (_e) {
    return null;
  }
};

const buildApiError = async (response, fallbackMessage, dataOverride = undefined) => {
  const data = dataOverride !== undefined ? dataOverride : await readResponseJsonSafe(response);
  const message = data?.error || data?.detail || data?.message || fallbackMessage;
  const err = new Error(message);
  err.status = response.status;
  err.data = data;
  err.code = data?.code || null;

  // Portal-blocked affiliates still have a session — surface a dedicated notice
  // instead of treating them as logged out / not an affiliate.
  if (response.status === 403 && isAffiliatePortalBlockedError(data)) {
    if (typeof window !== "undefined" && typeof window.dispatchEvent === "function") {
      try {
        window.dispatchEvent(
          new CustomEvent("affiliate-portal-blocked", { detail: { status: response.status, data } })
        );
      } catch (_e) {
        // ignore
      }
    }
    return err;
  }

  // Keep affiliate UI auth state consistent with backend.
  if (
    (response.status === 401 || response.status === 403) &&
    typeof window !== "undefined" &&
    typeof window.dispatchEvent === "function"
  ) {
    try {
      window.dispatchEvent(new CustomEvent("affiliate-auth-expired", { detail: { status: response.status, data } }));
    } catch (_e) {
      // ignore
    }
  }
  return err;
};

export const affiliateLogin = async ({ email, password }) => {
  const response = await apiFetch("/api/auth/login/", {
    method: "POST",
    credentials: "include",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ email, password }),
  });
  const data = await readResponseJsonSafe(response);
  if (response.ok) return data;
  throw await buildApiError(response, "Login failed", data);
};

export const affiliateCheck = async () => {
  const response = await apiFetch("/api/influencer/check/", { credentials: "include" });
  const data = await readResponseJsonSafe(response);
  if (response.ok) return data;
  throw await buildApiError(response, "Affiliate check failed", data);
};

export const fetchAffiliateTermsStatus = async () => {
  const response = await apiFetch("/api/influencer/terms/status/", { credentials: "include" });
  const data = await readResponseJsonSafe(response);
  if (response.ok) return data;
  throw await buildApiError(response, "Failed to fetch affiliate terms status", data);
};

export const acceptAffiliateTerms = async ({ version } = {}) => {
  const response = await apiFetch("/api/influencer/terms/accept/", {
    method: "POST",
    credentials: "include",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ version, accepted: true }),
  });
  const data = await readResponseJsonSafe(response);
  if (response.ok) return data;
  throw await buildApiError(response, "Failed to accept affiliate terms", data);
};

export const acceptPartnerTerms = async ({ version } = {}) => {
  const response = await apiFetch("/api/influencer/partner-terms/accept/", {
    method: "POST",
    credentials: "include",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ version, accepted: true }),
  });
  const data = await readResponseJsonSafe(response);
  if (response.ok) return data;
  throw await buildApiError(response, "Failed to accept partner terms", data);
};

/**
 * Public subscription plans, used by Buy Coupons to explain the Plus/Pro difference
 * with the same feature table the account settings page shows.
 */
export const fetchSubscriptionPlans = async () => {
  const response = await apiFetch("/api/subscription/plans/", { credentials: "include" });
  const data = await readResponseJsonSafe(response);
  if (response.ok) return data;
  throw await buildApiError(response, "Failed to fetch subscription plans", data);
};

export const fetchCouponPackQuote = async ({ months, plus_count, pro_count } = {}) => {
  const params = new URLSearchParams();
  params.append("months", String(months));
  params.append("plus_count", String(plus_count));
  params.append("pro_count", String(pro_count));

  const response = await apiFetch(`/api/influencer/coupon-packs/quote/?${params.toString()}`, {
    credentials: "include",
  });
  const data = await readResponseJsonSafe(response);
  if (response.ok) return data;
  throw await buildApiError(response, "Failed to fetch coupon pack quote", data);
};

export const createCouponPackCheckout = async ({ months, plus_count, pro_count, prefix } = {}) => {
  const response = await apiFetch("/api/influencer/coupon-packs/checkout/", {
    method: "POST",
    credentials: "include",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ months, plus_count, pro_count, prefix }),
  });
  const data = await readResponseJsonSafe(response);
  if (response.ok) return data;
  throw await buildApiError(response, "Failed to start coupon pack checkout", data);
};

export const fetchAffiliateCoupons = async ({ start_date = null, end_date = null } = {}) => {
  const params = new URLSearchParams();
  if (start_date) params.append("start_date", start_date);
  if (end_date) params.append("end_date", end_date);
  const url = params.toString() ? `/api/influencer/coupons/?${params.toString()}` : "/api/influencer/coupons/";

  const response = await apiFetch(url, { credentials: "include" });
  const data = await readResponseJsonSafe(response);
  if (response.ok) return data;
  throw await buildApiError(response, "Failed to fetch affiliate coupons", data);
};

export const fetchAffiliateAnalyticsSummary = async ({ start_date = null, end_date = null, coupon_ids = [] } = {}) => {
  const params = new URLSearchParams();
  if (start_date) params.append("start_date", start_date);
  if (end_date) params.append("end_date", end_date);
  if (coupon_ids && coupon_ids.length) params.append("coupon_ids", coupon_ids.join(","));
  const url = params.toString()
    ? `/api/influencer/analytics/summary/?${params.toString()}`
    : "/api/influencer/analytics/summary/";

  const response = await apiFetch(url, { credentials: "include" });
  const data = await readResponseJsonSafe(response);
  if (response.ok) return data;
  throw await buildApiError(response, "Failed to fetch affiliate analytics", data);
};

export const fetchAffiliateAnalyticsTimeseries = async ({
  granularity,
  start_date = null,
  end_date = null,
  coupon_ids = [],
} = {}) => {
  const params = new URLSearchParams();
  params.append("granularity", granularity);
  if (start_date) params.append("start_date", start_date);
  if (end_date) params.append("end_date", end_date);
  if (coupon_ids && coupon_ids.length) params.append("coupon_ids", coupon_ids.join(","));

  const response = await apiFetch(`/api/influencer/analytics/timeseries/?${params.toString()}`, { credentials: "include" });
  const data = await readResponseJsonSafe(response);
  if (response.ok) return data;
  throw await buildApiError(response, "Failed to fetch affiliate analytics timeseries", data);
};

export const fetchAffiliatePayments = async ({
  start_date = null,
  end_date = null,
  limit = null,
  offset = null,
} = {}) => {
  const params = new URLSearchParams();
  if (start_date) params.append("start_date", start_date);
  if (end_date) params.append("end_date", end_date);
  if (limit != null) params.append("limit", String(limit));
  if (offset != null) params.append("offset", String(offset));
  const url = params.toString()
    ? `/api/influencer/analytics/payments/?${params.toString()}`
    : "/api/influencer/analytics/payments/";

  const response = await apiFetch(url, { credentials: "include" });
  const data = await readResponseJsonSafe(response);
  if (response.ok) return data;
  throw await buildApiError(response, "Failed to fetch affiliate payments", data);
};

export const fetchAffiliateEarningsSummary = async ({ months = null } = {}) => {
  const params = new URLSearchParams();
  if (months != null) params.append("months", String(months));
  const url = params.toString()
    ? `/api/influencer/earnings/summary/?${params.toString()}`
    : "/api/influencer/earnings/summary/";

  const response = await apiFetch(url, { credentials: "include" });
  const data = await readResponseJsonSafe(response);
  if (response.ok) return data;
  throw await buildApiError(response, "Failed to fetch affiliate earnings summary", data);
};

export const fetchPayoutStatus = async () => {
  const response = await apiFetch("/api/influencer/payouts/status/", { credentials: "include" });
  const data = await readResponseJsonSafe(response);
  if (response.ok) return data;
  throw await buildApiError(response, "Failed to fetch payout status", data);
};

export const createPayoutOnboardingLink = async () => {
  const response = await apiFetch("/api/influencer/payouts/onboarding-link/", {
    method: "POST",
    credentials: "include",
    headers: { "Content-Type": "application/json" },
  });
  const data = await readResponseJsonSafe(response);
  if (response.ok) return data;
  throw await buildApiError(response, "Failed to create payout onboarding link", data);
};

export const fetchPayoutCurrentMonth = async () => {
  const response = await apiFetch("/api/influencer/payouts/current-month/", { credentials: "include" });
  const data = await readResponseJsonSafe(response);
  if (response.ok) return data;
  throw await buildApiError(response, "Failed to fetch current month payout estimate", data);
};

export const fetchPayoutPeriods = async ({ limit = null, offset = null } = {}) => {
  const params = new URLSearchParams();
  if (limit != null) params.append("limit", String(limit));
  if (offset != null) params.append("offset", String(offset));
  const url = params.toString()
    ? `/api/influencer/payouts/periods/?${params.toString()}`
    : "/api/influencer/payouts/periods/";

  const response = await apiFetch(url, { credentials: "include" });
  const data = await readResponseJsonSafe(response);
  if (response.ok) return data;
  throw await buildApiError(response, "Failed to fetch payout history", data);
};

export const fetchPayoutPeriodEvents = async (periodId) => {
  const response = await apiFetch(`/api/influencer/payouts/periods/${periodId}/events/`, {
    credentials: "include",
  });
  const data = await readResponseJsonSafe(response);
  if (response.ok) return data;
  throw await buildApiError(response, "Failed to fetch payout status history", data);
};

/**
 * Access coupons that can still be e-mailed: owned by the caller, effectively
 * active, not redeemed yet and not already sent to someone.
 * → `{ available: [{ id, code, free_access_plan, free_access_days, free_access_until }], count }`
 */
export const fetchAvailableAccessCoupons = async () => {
  const response = await apiFetch("/api/influencer/access-coupons/available/", {
    credentials: "include",
  });
  const data = await readResponseJsonSafe(response);
  if (response.ok) return data;
  throw await buildApiError(response, "Failed to fetch available access coupons", data);
};

/**
 * Bulk-send access coupons.
 *
 * @param {Array<{email: string, coupon_ids: number[]}>} recipients
 * → `{ results: [{ email, status, coupon_codes, error }], sent_count, skipped_count, failed_count }`
 *
 * The endpoint answers 200 with per-recipient outcomes; 400 is reserved for a
 * malformed request or an exceeded request/day cap.
 */
export const sendAccessCoupons = async (recipients) => {
  const response = await apiFetch("/api/influencer/access-coupons/send/", {
    method: "POST",
    credentials: "include",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ recipients }),
  });
  const data = await readResponseJsonSafe(response);
  if (response.ok) return data;
  throw await buildApiError(response, "Failed to send access coupons", data);
};

/** Single regular (non-access) coupon to one recipient. */
export const sendRegularCoupon = async (email, couponId) => {
  const response = await apiFetch("/api/influencer/coupons/send/", {
    method: "POST",
    credentials: "include",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ email, coupon_id: couponId }),
  });
  const data = await readResponseJsonSafe(response);
  if (response.ok) return data;
  throw await buildApiError(response, "Failed to send coupon", data);
};

/** History of coupon e-mails sent by the caller. */
export const fetchCouponSends = async ({ kind = "access", limit = null, offset = null } = {}) => {
  const params = new URLSearchParams();
  if (kind) params.append("kind", kind);
  if (limit != null) params.append("limit", String(limit));
  if (offset != null) params.append("offset", String(offset));
  const url = params.toString()
    ? `/api/influencer/coupon-sends/?${params.toString()}`
    : "/api/influencer/coupon-sends/";

  const response = await apiFetch(url, { credentials: "include" });
  const data = await readResponseJsonSafe(response);
  if (response.ok) return data;
  throw await buildApiError(response, "Failed to fetch coupon sends", data);
};

/**
 * How many access coupons each address already received from the caller.
 * → `{ counts: { "a@b.com": 2 } }` (zero counts are omitted).
 */
export const fetchRecipientCounts = async (emails) => {
  const response = await apiFetch("/api/influencer/coupon-sends/recipient-counts/", {
    method: "POST",
    credentials: "include",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ emails }),
  });
  const data = await readResponseJsonSafe(response);
  if (response.ok) return data;
  throw await buildApiError(response, "Failed to fetch recipient counts", data);
};

export const setAffiliatePromotedCoupon = async ({ coupon_id }) => {
  const response = await apiFetch("/api/influencer/promoted-coupon/", {
    method: "POST",
    credentials: "include",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ coupon_id }),
  });
  const data = await readResponseJsonSafe(response);
  if (response.ok) return data;
  throw await buildApiError(response, "Failed to set promoted coupon", data);
};


